import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, useLocation } from "react-router-dom";
import msalInstance from './authConfig';
import { MsalProvider, useMsalAuthentication, useIsAuthenticated } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import Loader from './component/Common/Loader';
import LoggedOut from './pages/LoggedOut';

import 'react-toastify/dist/ReactToastify.css';
// Keep the full app shell in a separate chunk so it is not requested
// until MSAL confirms the user is authenticated.
// This reduces unauthenticated exposure of non-essential app code.
const AuthenticatedShell = lazy(() => import('./AuthenticatedShell'));

const RequireAuthAndLoadApp = () => {
  // Triggers MSAL redirect login when a valid session is not present.
  // The hook also handles the redirect response lifecycle.
  useMsalAuthentication(InteractionType.Redirect);
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) {
    // While auth is in progress or missing, render only a minimal loader.
    // Important: do not mount the main app shell before auth succeeds.
    return <Loader />;
  }

  return (
    // Once authenticated, load the protected shell chunk and show a loader
    // while that chunk is fetched and initialized.
    <Suspense fallback={<Loader />}>
      <AuthenticatedShell />
    </Suspense>
  );
};

const AuthGate = () => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();

  // Preserve explicit logged-out route behavior:
  // - unauthenticated users can see the logged-out view
  // - authenticated users are redirected back to the app root
  if (location.pathname === '/logged-out') {
    if (isAuthenticated) {
      return <Navigate to="/" replace />;
    }
    return <LoggedOut />;
  }

  // All other routes require auth before protected shell loading.
  return <RequireAuthAndLoadApp />;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    // Keep bootstrap surface minimal here: auth provider + router + gate.
    // Redux store and App are intentionally moved to AuthenticatedShell.
    <MsalProvider instance={msalInstance}>
        <BrowserRouter>
             <AuthGate />
        </BrowserRouter>
    </MsalProvider>
);